// Protected accounts (see PROTECTED_USER_EMAILS server-side) need the shared
// admin passcode before a role change or delete. The passcode is verified
// server-side; the one-shot token it returns is what authorizes the follow-up
// call, so skipping this dialog gets you a 403 either way.
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { verifyAdminPasscode } from "@/lib/api";

export default function PasscodeDialog({ label, onClose, onVerified }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await verifyAdminPasscode(code);
      onVerified(data.verify_token);
    } catch (err) {
      toast.error(err?.response?.status === 429
        ? (err.response?.data?.detail || "Too many attempts. Try again shortly.")
        : "Incorrect passcode");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="dialog-user-passcode">
        <DialogHeader>
          <DialogTitle>Passcode Required</DialogTitle>
          <DialogDescription>This account is protected. Enter the admin passcode to {label}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Admin Passcode</Label>
            <Input
              type="password" inputMode="numeric" required autoFocus value={code}
              onChange={(e) => setCode(e.target.value)} data-testid="input-user-passcode"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="btn-cancel-user-passcode">Cancel</Button>
            <Button type="submit" disabled={busy || !code} data-testid="btn-submit-user-passcode">
              {busy ? "Checking…" : "Confirm"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
